import { useState } from 'react';
import {
  RefreshCw,
  Filter,
  AlertTriangle,
  Clock,
  DollarSign,
  CalendarDays,
  Plus,
  Bell,
} from 'lucide-react';
import {
  useUpcomingRenewals,
  useCreateRenewalDeal,
  useProcessRenewalReminders,
  type RenewalFilters,
  type RenewalDeal,
} from '../hooks/useRenewals';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface RenewalsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

function getUrgencyColor(daysRemaining: number): string {
  if (daysRemaining < 30) return 'red';
  if (daysRemaining < 60) return 'orange';
  return 'green';
}

function getUrgencyBg(daysRemaining: number): string {
  if (daysRemaining < 30) return 'bg-red-50 border-red-100';
  if (daysRemaining < 60) return 'bg-amber-50 border-amber-100';
  return '';
}

const statusLabels: Record<string, { label: string; color: string }> = {
  upcoming: { label: 'Upcoming', color: 'blue' },
  in_progress: { label: 'In Progress', color: 'orange' },
  renewed: { label: 'Renewed', color: 'green' },
  lost: { label: 'Lost', color: 'red' },
  expired: { label: 'Expired', color: 'gray' },
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function RenewalsView({ onNavigate }: RenewalsViewProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<RenewalFilters>({ daysAhead: 120 });

  const { data, isLoading, error } = useUpcomingRenewals(filters);
  const createRenewalDeal = useCreateRenewalDeal();
  const processReminders = useProcessRenewalReminders();

  const renewals = data?.renewals ?? [];
  const summary = data?.summary;

  function handleCreateRenewal(deal: RenewalDeal) {
    createRenewalDeal.mutate(deal.dealId);
  }

  // --- Summary Cards ---
  function renderSummary() {
    if (!summary) return null;

    const cards = [
      {
        label: 'Renewals Due',
        value: summary.totalRenewals,
        icon: CalendarDays,
        color: 'text-brand-600 bg-brand-100',
      },
      {
        label: 'Premium at Risk',
        value: formatCurrency(summary.totalPremiumAtRisk),
        icon: DollarSign,
        color: 'text-green-600 bg-green-100',
      },
      {
        label: 'Avg Days to Expiry',
        value: Math.round(summary.avgDaysToExpiry),
        icon: Clock,
        color: 'text-blue-600 bg-blue-100',
      },
      {
        label: 'Urgent (<30 days)',
        value: summary.urgentCount,
        icon: AlertTriangle,
        color: 'text-red-600 bg-red-100',
      },
    ];

    return (
      <div className="grid grid-cols-4 gap-4 mb-6">
        {cards.map((card) => (
          <div key={card.label} className="card p-4">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-lg',
                  card.color,
                )}
              >
                <card.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                <p className="text-xs text-slate-500">{card.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Renewals</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data
              ? `${data.total} upcoming renewal${data.total !== 1 ? 's' : ''}`
              : 'Track policy renewals'}
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => processReminders.mutate()}
          disabled={processReminders.isPending}
        >
          <Bell className="h-4 w-4" />
          {processReminders.isPending ? 'Processing...' : 'Process Reminders'}
        </button>
      </div>

      {/* Filters Bar */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex rounded-lg border border-surface-200 p-0.5">
            {[30, 60, 90, 120].map((days) => (
              <button
                key={days}
                className={cn(
                  'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                  filters.daysAhead === days
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:text-slate-900',
                )}
                onClick={() => setFilters((f) => ({ ...f, daysAhead: days }))}
              >
                {days} Days
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'btn-secondary btn-sm',
              showFilters && 'bg-brand-50 border-brand-200 text-brand-700',
            )}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 flex items-center gap-3">
            <div>
              <label className="label mb-1">Status</label>
              <select
                className="input py-1.5"
                value={filters.status ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, status: e.target.value || undefined }))
                }
              >
                <option value="">All</option>
                {Object.entries(statusLabels).map(([value, cfg]) => (
                  <option key={value} value={value}>
                    {cfg.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load renewals. Please try again.
          </div>
        ) : isLoading ? (
          <>
            {/* Skeleton summary */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="card p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 animate-pulse rounded-lg bg-surface-200" />
                    <div>
                      <div className="h-6 w-16 animate-pulse rounded bg-surface-200" />
                      <div className="mt-1 h-3 w-24 animate-pulse rounded bg-surface-100" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Skeleton table */}
            <div className="card">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 border-b border-surface-100 px-4 py-3 last:border-0"
                >
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="h-4 w-32 animate-pulse rounded bg-surface-100" />
                  <div className="flex-1" />
                  <div className="h-4 w-20 animate-pulse rounded bg-surface-200" />
                </div>
              ))}
            </div>
          </>
        ) : renewals.length > 0 ? (
          <>
            {renderSummary()}

            {/* Table */}
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-200 bg-surface-50">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Deal
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Organization
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Expiration
                    </th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Days Left
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Premium
                    </th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Status
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100">
                  {renewals.map((renewal) => {
                    const urgencyColor = getUrgencyColor(renewal.daysRemaining);
                    const rowBg = getUrgencyBg(renewal.daysRemaining);
                    const sCfg = statusLabels[renewal.status] ?? statusLabels.upcoming;

                    return (
                      <tr
                        key={renewal.id}
                        className={cn(
                          'transition-colors hover:bg-surface-50',
                          rowBg,
                        )}
                      >
                        {/* Deal Name */}
                        <td className="px-4 py-3">
                          <button
                            className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline text-left"
                            onClick={() =>
                              onNavigate('deal-detail', {
                                dealId: renewal.dealId,
                              })
                            }
                          >
                            {renewal.dealName}
                          </button>
                          {renewal.policyNumber && (
                            <p className="text-xs text-slate-400">
                              Policy: {renewal.policyNumber}
                            </p>
                          )}
                        </td>

                        {/* Org */}
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {renewal.organizationName ?? '--'}
                        </td>

                        {/* Expiration Date */}
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {new Date(renewal.expirationDate).toLocaleDateString()}
                        </td>

                        {/* Days Remaining */}
                        <td className="px-4 py-3 text-center">
                          <Badge color={urgencyColor} size="sm">
                            {renewal.daysRemaining}d
                          </Badge>
                        </td>

                        {/* Premium */}
                        <td className="px-4 py-3 text-right text-sm font-medium text-slate-900">
                          {formatCurrency(renewal.premium)}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 text-center">
                          <Badge color={sCfg.color} size="sm">
                            {sCfg.label}
                          </Badge>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3 text-right">
                          {(renewal.status === 'upcoming' ||
                            renewal.status === 'expired') && (
                            <button
                              onClick={() => handleCreateRenewal(renewal)}
                              disabled={createRenewalDeal.isPending}
                              className="btn-secondary btn-sm"
                              title="Create renewal deal"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Renew
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <EmptyState
            icon={RefreshCw}
            title="No upcoming renewals"
            description="No policies are due for renewal in this time period."
          />
        )}
      </div>
    </div>
  );
}
